import { message, Modal, Upload as UploadAntd } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { PlusOutlined } from '@ant-design/icons';
import axios from "axios";
import apiPath from "../constants/apiPath";

// Uploads now go through the backend (POST /admin/media/upload), which
// holds the real AWS credentials server-side and never exposes them. This
// used to upload straight to S3 from the browser with a hardcoded access
// key + secret baked into this file — visible to anyone via view-source on
// the deployed site, no login required. See MediaUploadController.ts.

const MB = 1024 * 1024;
const KB = 1024;
const LINKEDIN_UPLOAD_LIMITS = {
  image: {
    maxBytes: 5 * MB,
    maxPixels: 36152320,
    label: "Images: JPG, PNG, GIF or WEBP, max 5 MB and 36,152,320 pixels",
  },
  video: {
    minBytes: 75 * KB,
    maxBytes: 5 * 1024 * MB,
    minDuration: 3,
    maxDuration: 15 * 60,
    minWidth: 256,
    minHeight: 144,
    maxWidth: 4096,
    maxHeight: 2304,
    minAspectRatio: 1 / 2.4,
    maxAspectRatio: 2.4,
    label: "Videos: MP4, 75 KB to 5 GB, 3 seconds to 15 minutes, 256x144 to 4096x2304",
  },
  document: {
    maxBytes: 100 * MB,
    label: "PDF documents: max 100 MB",
  },
};

const getUploadLimitType = (file) => {
  if (file?.type?.startsWith("image/")) return "image";
  if (file?.type?.startsWith("video/")) return "video";
  if (file?.type === "application/pdf") return "document";
  return "file";
};

const getLimitText = (fileType = []) => {
  const limits = [];
  if (fileType.some((type) => type.startsWith("image/"))) {
    limits.push(LINKEDIN_UPLOAD_LIMITS.image.label);
  }
  if (fileType.some((type) => type.startsWith("video/"))) {
    limits.push(LINKEDIN_UPLOAD_LIMITS.video.label);
  }
  if (fileType.includes("application/pdf")) {
    limits.push(LINKEDIN_UPLOAD_LIMITS.document.label);
  }
  return limits.join(" | ");
};

const getImagePixelCount = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img.width * img.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read image dimensions"));
    };
    img.src = objectUrl;
  });

const getVideoMetadata = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read video metadata"));
    };
    video.src = objectUrl;
  });

// Images over LinkedIn's 5 MB cap are shrunk automatically (re-encoded as JPEG,
// quality then size stepped down) instead of being rejected, so a big photo
// never blocks an upload. GIFs are left alone — re-encoding would drop the
// animation.
const compressImageUnderLimit = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      try {
        const target = Math.floor(maxBytes * 0.95);
        let width = img.naturalWidth;
        let height = img.naturalHeight;
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(width));
          canvas.height = Math.max(1, Math.round(height));
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          for (const quality of [0.92, 0.85, 0.75, 0.65]) {
            const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
            if (blob && blob.size <= target) {
              URL.revokeObjectURL(objectUrl);
              const name = String(file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
              const out = new File([blob], name, { type: "image/jpeg" });
              out.uid = file.uid;
              resolve(out);
              return;
            }
          }
          width *= 0.8;
          height *= 0.8;
        }
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Could not compress image"));
      } catch (e) {
        URL.revokeObjectURL(objectUrl);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read image"));
    };
    img.src = objectUrl;
  });

const MultipleImageUpload = ({ data, fileType, imageType, btnName, onDelete, onChange, maxCount = 20 }) => {
  const supportedImageExtensions = [".png", ".jpg", ".jpeg", ".avif", ".webp", ".gif"];
  const supportedVideoExtensions = [".mp4", ".ogg", ".webm", ".mpeg"];
  const [fileList, setFileList] = useState([]);
  // Uploads currently in flight. While any are running, the `data` prop (a
  // lagging snapshot of what the parent has seen so far) must not overwrite the
  // local list, or finished files from a multi-file pick get dropped.
  const inFlightRef = useRef(0);
  const limitText = getLimitText(fileType);

  const beforeUpload = async (file) => {
    if (fileType.includes(file.type)) {

    } else {
      message.error("File format is not correct")
      return false
    }

    const limitType = getUploadLimitType(file);
    const limits = LINKEDIN_UPLOAD_LIMITS[limitType];

    if (limits?.minBytes && file.size < limits.minBytes) {
      message.error("Video must be at least 75 KB for LinkedIn.")
      return false;
    }

    let uploadFile = file;
    if (limits?.maxBytes && file.size > limits.maxBytes) {
      if (limitType === "image" && file.type !== "image/gif") {
        try {
          uploadFile = await compressImageUnderLimit(file, limits.maxBytes);
          message.info(`"${file.name}" was over 5 MB, so it was compressed automatically.`);
        } catch (err) {
          message.error(`${limits.label}.`);
          return false;
        }
      } else {
        message.error(`${limits.label}.`);
        return false;
      }
    }

    if (limitType === "image") {
      try {
        const pixelCount = await getImagePixelCount(file);
        if (pixelCount > LINKEDIN_UPLOAD_LIMITS.image.maxPixels) {
          message.error(LINKEDIN_UPLOAD_LIMITS.image.label)
          return false;
        }
      } catch (err) {
        message.error("Could not validate image dimensions.")
        return false;
      }
    }

    if (limitType === "video") {
      try {
        const { duration, width, height } = await getVideoMetadata(file);
        const aspectRatio = width && height ? width / height : 0;

        if (
          duration < LINKEDIN_UPLOAD_LIMITS.video.minDuration ||
          duration > LINKEDIN_UPLOAD_LIMITS.video.maxDuration
        ) {
          message.error("Video duration must be between 3 seconds and 15 minutes for LinkedIn.")
          return false;
        }

        if (
          width < LINKEDIN_UPLOAD_LIMITS.video.minWidth ||
          height < LINKEDIN_UPLOAD_LIMITS.video.minHeight ||
          width > LINKEDIN_UPLOAD_LIMITS.video.maxWidth ||
          height > LINKEDIN_UPLOAD_LIMITS.video.maxHeight
        ) {
          message.error("Video resolution must be between 256x144 and 4096x2304 for LinkedIn.")
          return false;
        }

        if (
          aspectRatio < LINKEDIN_UPLOAD_LIMITS.video.minAspectRatio ||
          aspectRatio > LINKEDIN_UPLOAD_LIMITS.video.maxAspectRatio
        ) {
          message.error("Video aspect ratio must be between 1:2.4 and 2.4:1 for LinkedIn.")
          return false;
        }
      } catch (err) {
        message.error("Could not validate video metadata.")
        return false;
      }
    }

    return uploadFile
  }

  useEffect(() => {
    if (!data) return;
    if (inFlightRef.current > 0) return;
    console.log(data, "gdfdsjhagsdgf")
    const multipleFileList = data.map((item, index) => ({
      uid: `${index + 1}`,
      name: typeof item === "string" ? `File-${index + 1}` : item.name || `File-${index + 1}`,
      status: "done",
      url: typeof item === "string" ? item : item.url,
    }));
    console.log(multipleFileList);
    setFileList(multipleFileList)
  }, [data])

  // Proxies through the backend instead of uploading straight to S3 — see
  // the import comment above. `.Location` on the return value is kept so
  // handleImgChange below (which expects that shape from the old AWS SDK
  // call) didn't need to change. onProgress reports real upload progress
  // (bytes sent, not a fake timer), so a large video shows an actually-
  // advancing bar instead of the upload looking frozen — previously
  // nothing appeared in the list at all until the whole thing finished,
  // which made a slow video upload look stalled.
  const uploadFileToS3 = async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${apiPath.baseURL}admin/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      return { Location: res.data?.data?.url };
    } catch (err) {
      throw err;
    }
  };

  const handleImgChange = async (event) => {

    const { file } = event;
    const isDuplicate = fileList.some(existingFile => existingFile.name === file.name);
    if (isDuplicate) {
      message.warning("This file is already uploaded.");
      return;
    }

    // Placeholder shown immediately, before the upload has even started —
    // antd renders this "uploading" status as a spinner + progress bar on
    // the picture-card, so there's visible feedback right away instead of
    // the file only appearing once the whole upload is already done.
    setFileList((prevFileList) => [
      ...prevFileList,
      { uid: file.uid, name: file.name, status: "uploading", percent: 0 },
    ]);

    inFlightRef.current += 1;
    try {
      const data = await uploadFileToS3(file, (percent) => {
        setFileList((prevFileList) =>
          prevFileList.map((item) =>
            item.uid === file.uid ? { ...item, percent } : item
          )
        );
      });
      const fileData = {
        uid: file.uid,
        name: file.name,
        status: 'done',
        url: data.Location,
        thumbUrl: data.Location,
      };

      // Use the functional updater form, not the outer `fileList` closure:
      // when two files are picked together, both uploads finish around the
      // same time, and reading the outer `fileList` would have both calls
      // append to the same stale (pre-upload) list, so the second one to
      // finish silently overwrites the first instead of both accumulating.
      setFileList((prevFileList) => {
        const updatedFileList = prevFileList.map((item) =>
          item.uid === file.uid ? fileData : item
        );

        if (onChange) {
          onChange(updatedFileList.filter((f) => f.url));
        }

        return updatedFileList;
      });
    } catch (err) {
      console.error(err);
      message.error("Upload failed. Please try again.");
      setFileList((prevFileList) =>
        prevFileList.filter((item) => item.uid !== file.uid)
      );
    } finally {
      inFlightRef.current -= 1;
    }
  };


  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const uploadButton = (
    <div> <PlusOutlined /> <div style={{ marginTop: 8 }}>Upload {btnName}</div> </div>
  );

  const handleRemove = (file) => {
    console.log(fileList, file);

    const newFileList = fileList.filter(item => item.uid !== file.uid);
    console.log(newFileList);

    setFileList(newFileList);

    if (onChange) {
      onChange(newFileList); // Use newFileList instead of undefined variable
    }

    // onDelete(file.url);
  };

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVideo, setPreviewVideo] = useState(null);

  const handleCancel = () => {
    setPreviewVisible(false);
    setPreviewVideo();
    setPreviewImage();
  }
  const handlePreview = async (file) => {
    console.log(file, "file")
    let video = supportedVideoExtensions.some((ext) => file?.url?.toLowerCase().endsWith(ext))
    console.log(video, "video")
    if (video) {
      setPreviewVideo(file?.url || file?.thumbUrl);

    } else {
      console.log(file?.url || file?.thumbUrl)
      setPreviewImage(file?.url || file?.thumbUrl);
    }
    setPreviewVisible(true);
  };

  return (
    <>
      <UploadAntd
        listType="picture-card"
        onRemove={handleRemove}
        onPreview={handlePreview}
        maxCount={maxCount}
        beforeUpload={beforeUpload}
        fileList={fileList}
        // onChange={handleChange}
        customRequest={handleImgChange}
        itemRender={(originNode, file) => {
          // antd's picture-card list only knows how to thumbnail images —
          // for videos it renders a broken <img>, so swap in a real <video>
          // thumbnail here instead; images keep antd's default rendering.
          const isVideoFile = supportedVideoExtensions.some((ext) =>
            (file?.url || file?.thumbUrl || "").toLowerCase().includes(ext)
          );

          if (!isVideoFile) return originNode;

          const children = React.Children.toArray(originNode.props.children);
          children[0] = (
            <video
              key="video-thumb"
              className="custom-upload-item-video-thumb"
              src={file?.url || file?.thumbUrl}
              muted
              onClick={() => handlePreview(file)}
            />
          );

          return React.cloneElement(originNode, {}, children);
        }}
      >
        {fileList.length >= maxCount ? null : uploadButton}
      </UploadAntd>
      {limitText && (
        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
          LinkedIn limits: {limitText}
        </div>
      )}
      <Modal
        bodyStyle={{ padding: "0px !important", width: "auto !important", background: "black" }}
        // style={{ padding: "0px !important", background: "black" }}
        open={previewVisible}
        title={''}
        footer={null}
        onCancel={handleCancel}
      >
        {previewVideo ? <video controls src={previewVideo} /> : <img alt="example" style={{ minWidth: "100%" }} src={previewImage} />}
      </Modal>
    </>
  );
};

export default MultipleImageUpload;
